/**
 * Scrolls to the first error field in a form
 * @param errors - Object containing field errors (key-value pairs)
 * @returns true if an error field was found and scrolled to, false otherwise
 */
export const scrollToFirstError = (errors: { [key: string]: string[] | string | undefined }): boolean => {
  if (!errors || Object.keys(errors).length === 0) {
    return false;
  }

  // Get the first error field name
  const firstErrorField = Object.keys(errors)[0];
  
  // Try multiple strategies to find the error element
  let errorElement: HTMLElement | null = null;
  
  // Strategy 1: Try to find input/select/textarea by id or name
  errorElement = document.getElementById(firstErrorField) || 
                 document.querySelector(`[name="${firstErrorField}"]`) as HTMLElement;
  
  // Strategy 2: Try to find by field-error class near the field
  if (!errorElement) {
    // For complex field names like "section-1-question-2-moment-3-moment"
    // Try to find the closest field-error element
    const fieldErrorElements = document.querySelectorAll('.field-error');
    if (fieldErrorElements.length > 0) {
      // Find the first field-error that contains text matching the error
      const firstError = Array.from(fieldErrorElements).find(el => {
        const errorText = errors[firstErrorField];
        if (typeof errorText === 'string') {
          return el.textContent?.includes(errorText);
        } else if (Array.isArray(errorText) && errorText.length > 0) {
          return el.textContent?.includes(errorText[0]);
        }
        return false;
      });
      
      if (firstError) {
        // Try to find the input near this error
        const inputContainer = firstError.closest('.form-row, .input-container, .field-container');
        if (inputContainer) {
          errorElement = inputContainer.querySelector('input, select, textarea') as HTMLElement;
        }
      }
    }
  }
  
  // Strategy 3: Find the field-error element itself and scroll to its parent container
  if (!errorElement) {
    const fieldErrorElements = document.querySelectorAll('.field-error');
    if (fieldErrorElements.length > 0) {
      const firstErrorEl = fieldErrorElements[0] as HTMLElement;
      const container = firstErrorEl.closest('.form-row, .input-container, .field-container') as HTMLElement;
      errorElement = container || firstErrorEl;
    }
  }
  
  if (errorElement) {
    // Scroll to the element with some offset from top
    const offset = 100; // Offset from top of viewport
    const elementPosition = errorElement.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - offset;
    
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
    
    // Focus the input if it's focusable
    if (errorElement instanceof HTMLInputElement || 
        errorElement instanceof HTMLSelectElement || 
        errorElement instanceof HTMLTextAreaElement) {
      setTimeout(() => {
        errorElement?.focus();
      }, 300);
    }
    
    return true;
  }
  
  return false;
};

